/**
 * Browser localStorage utilities for persisting conversationId per ticketId
 *
 * Maps ticketId -> conversationId so users can return to the same conversation
 * when revisiting a ticket.
 */

const STORAGE_KEY = "ticketcopilot-conversations";

/**
 * Retrieves the stored conversationId for a given ticketId
 *
 * @param ticketId - The ticket ID to look up
 * @returns The stored conversationId, or null if not found
 */
export function getStoredConversationId(ticketId: string): string | null {
  // Skip if running on server
  if (typeof window === "undefined") return null;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const conversationsMap: Record<string, string> = JSON.parse(stored);
    return conversationsMap[ticketId] || null;
  } catch (error) {
    console.error(
      "[ConversationStorage] Failed to read from localStorage:",
      error,
    );
    return null;
  }
}

/**
 * Stores a conversationId for a given ticketId
 *
 * @param ticketId - The ticket ID to associate with the conversation
 * @param conversationId - The conversation ID to store
 */
export function storeConversationId(
  ticketId: string,
  conversationId: string,
): void {
  // Skip if running on server
  if (typeof window === "undefined") return;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const conversationsMap: Record<string, string> = stored
      ? JSON.parse(stored)
      : {};

    // Update mapping
    conversationsMap[ticketId] = conversationId;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(conversationsMap));

    console.log(
      `[ConversationStorage] Stored conversationId for ticket ${ticketId}`,
    );
  } catch (error) {
    console.error(
      "[ConversationStorage] Failed to write to localStorage:",
      error,
    );
  }
}
